/* eslint-disable camelcase,no-continue */
import { MAILING_STATUSES } from "Constants/mailings";

import {NotixApi} from "Services/api";
import I18nService from "Services/i18n";
import { FetchRequest } from "./fetchRequest";
import { CommonAbortController } from "./abortController";

export function mailingStatusMap(value) {
  const done = I18nService.getStringByKey("sending_list_status-done");
  const draft = I18nService.getStringByKey("sending_list_status-draft");
  const sending = I18nService.getStringByKey("sending_list_status-sending");
  const failed = I18nService.getStringByKey("sending_list_status-failed");
  const paused = I18nService.getStringByKey("sending_list_status-paused");
  const unknown = I18nService.getStringByKey("sending_list_status-unknown");
  const scheduled = I18nService.getStringByKey("sending_list_status-scheduled");
  const regular = I18nService.getStringByKey("sending_list_status-regular");
  const rss = I18nService.getStringByKey("sending_list_status-rss");
  const rss_paused = I18nService.getStringByKey(
    "sending_list_status-rss_paused"
  );
  const rss_item = I18nService.getStringByKey("sending_list_status-rss_item");
  const rss_item_paused = I18nService.getStringByKey(
    "sending_list_status-rss_item_paused"
  );

  switch (value) {
    case MAILING_STATUSES.SEND:
      return done.toUpperCase();
    case MAILING_STATUSES.DRAFT:
      return draft.toUpperCase();
    case MAILING_STATUSES.QUEUED:
      return sending.toUpperCase();
    case MAILING_STATUSES.FAILED:
      return failed.toUpperCase();
    case MAILING_STATUSES.PAUSED:
      return paused.toUpperCase();
    case MAILING_STATUSES.SCHEDULED:
      return scheduled.toUpperCase();
    case MAILING_STATUSES.REGULAR:
      return regular.toUpperCase();
    case MAILING_STATUSES.RSS:
      return rss.toUpperCase();
    case MAILING_STATUSES.RSS_PAUSED:
      return rss_paused.toUpperCase();
    case MAILING_STATUSES.RSS_ITEM:
      return rss_item.toUpperCase();
    case MAILING_STATUSES.RSS_ITEM_PAUSED:
      return rss_item_paused.toUpperCase();
    default:
      return unknown.toUpperCase();
  }
}

export function applySendingsFilters(filters, state) {
  let keys = Object.keys(filters);
  let values = Object.values(filters);

  const prepareValues = (values, key) => {
    if (Array.isArray(values) && values?.length) {
      if (key === "geo" || key === "audience") {
        return values.map((value) => value.label);
      } else {
        return values.map((value) => +value.value);
      }
    } else {
      return values;
    }
  };

  const preparedValues = values.map((value, index) =>
    prepareValues(value, keys[index])
  );

  const prepareFilters = (keys, values) => {
    let prepared = [];
    for (let i = 0; i < keys?.length; i++) {
      prepared.push({
        type: keys[i],
        value: values[i],
        order: state
          ? state.find((item) => {
              return item.type === keys[i];
            })?.order
          : keys[i] === "sending_ids"
          ? "desc"
          : "",
      });
    }
    return prepared;
  };

  return prepareFilters(keys, preparedValues);
}

function loadedSendingsTransform(sendings, count, stats) {
  const result = [];
  for (let i = 0; i < sendings?.length; i += 1) {
    const sending = sendings[i];

    if (!sending) {
      continue;
    }

    const sending_message = sending.MessageTemplate;
    const sending_mailing = sending.Mailing;
    const sending_targeting = sending.Targeting;
    const sending_mailing_stats = sending.MailingStats;
    const sending_schedule = sending.Schedule;
    const sending_rss = sending.RssTemplate;
    const type_mailing = sending.type_mailing;
    const mailing_rules = sending.Rules.rules && {
      rules: JSON.parse(sending.Rules.rules),
    };

    const message_template = {
      id: sending_message.Id,
      name:
        sending_message.Name.length > 0
          ? sending_message.Name
          : "INVALID NAME(EMPTY)",
      pub_id: sending_message.PubId,
      title:
        sending_message.MessageContent && sending_message.MessageContent.title,
      text:
        sending_message.MessageContent && sending_message.MessageContent.text,
      icon:
        sending_message.MessageContent && sending_message.MessageContent.icon,
      img: sending_message.MessageContent && sending_message.MessageContent.img,
      url: sending_message.MessageContent && sending_message.MessageContent.url,
      languages: sending_message.MultiMessageContent,
      metadata: sending_message.Metadata,
    };

    const rss_template = {
      feed_url: sending_rss.feed_url,
      description: sending_rss.text,
      title: sending_rss.title,
      targetUrl: sending_rss.url,
      iconUrl: sending_rss.icon,
      imageUrl: sending_rss.img,
      url_postfix: sending_rss.url_postfix,
      item_ttl_sec: sending_rss.item_ttl_sec,
      sending_lifetime: sending_rss.sending_lifetime,
      gmt_offset: sending_rss.gmt_offset,
    };

    const mailing = {
      id: sending_mailing.Id,
      message_id: sending_mailing.MessageId,
      sending_id: sending_mailing.SendingId,
      status: sending_mailing.Status,
      zone_id: sending_mailing.ZoneId,
      ttl: sending_mailing.Ttl,
      source: sending_mailing.Source,
      is_archived: sending_mailing.IsArchived,
    };

    const mailing_schedule = {
      start_date: sending_schedule.start_date,
      interval: sending_schedule.interval,
    };

    const targeting = {
      id: sending_targeting.id,
      mailing_id: sending_targeting.mailing_id,
      targeting:
        sending_targeting.targeting && JSON.parse(sending_targeting.targeting),
    };

    const mailing_stats = {
      start_date: sending_mailing_stats.start_date,
    };

    result.push({
      message_template,
      mailing,
      targeting,
      mailing_stats,
      mailing_schedule,
      rss_template,
      type_mailing,
      mailing_rules,
    });
  }
  return { result, count, stats };
}

function responseAction(sending) {
  return {
    message_id: sending.message_id,
    sending_id: sending.sending_id,
    status: sending.status,
    mailing_status: sending.mailing_status,
    start_date: sending.start_date,
  };
}

class Sendings {
  integrateRss(body) {
    return NotixApi.post(`/sendings/rss/create`, body).then((result) => {
      return result;
    });
  }

  updateIntegrationRss(body) {
    return NotixApi.post(`/sendings/rss/update`, body).then((result) => {
      return result;
    });
  }

  createSending(body) {
    return NotixApi.post(`/sendings/create`, body).then((result) => {
      return result;
    });
  }

  updateSending(body) {
    return NotixApi.post(`/sendings/update`, body).then((result) => {
      return result;
    });
  }

  flagsSending(mailingId, body) {
    return NotixApi.post(
      `/sendings/flags?mailingId=${mailingId}`,
      body
    ).then((result) => {
      return result;
    });
  }

  async loadSendings(offset, limit, body) {
    const isAborted = CommonAbortController.controller.signal.aborted;

    if (isAborted) {
      CommonAbortController.createController();
    }

    const result = await FetchRequest({
      url: `/ssp/sendings/filtered?offset=${offset}&limit=${limit}`,
      method: "POST",
      useToken: true,
      body,
      controller: CommonAbortController.controller,
    });

    return loadedSendingsTransform(result.sendings, result.count, result.stats);
  }

  startSending(mailingId) {
    return NotixApi.post(`/sendings/start?mailingId=${mailingId}`).then(
      (result) => {
        return responseAction(result);
      }
    );
  }

  pauseSending(mailingId) {
    return NotixApi.post(`/sendings/pause?mailingId=${mailingId}`).then(
      (result) => {
        return responseAction(result);
      }
    );
  }
}

const sendingsInstance = new Sendings();

export default sendingsInstance;